import {
  Accordion,
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Group,
  Modal,
  SegmentedControl,
  Skeleton,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconNotes } from '@tabler/icons-react';
import { useState } from 'react';

import { useNotes, type RefreshInterval } from '../api/queries';
import type { NoteKind, NoteStatusFilter } from '../api/types';
import { formatDate, formatFullDateTime, today } from '../lib/format';
import { PageControls } from './PageControls';

const PAGE_SIZE = 20;
const KIND_LABEL: Record<NoteKind, string> = {
  question: 'Вопрос',
  rule: 'Правило',
  decision: 'Решение',
  context: 'Контекст',
};

export function NotesButton({ refreshInterval }: { refreshInterval: RefreshInterval }) {
  const [opened, setOpened] = useState(false);

  return (
    <>
      <Tooltip label="Заметки" withArrow>
        <ActionIcon
          variant="default"
          size="lg"
          aria-label="Открыть заметки"
          aria-haspopup="dialog"
          onClick={() => setOpened(true)}
        >
          <IconNotes size={18} stroke={1.5} />
        </ActionIcon>
      </Tooltip>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={<Text fw={600}>Заметки</Text>}
        size="lg"
        padding="lg"
        centered
      >
        {opened && <NotesContent refreshInterval={refreshInterval} />}
      </Modal>
    </>
  );
}

function NotesContent({ refreshInterval }: { refreshInterval: RefreshInterval }) {
  const [status, setStatus] = useState<NoteStatusFilter>('open');
  const [page, setPage] = useState(0);
  const { data, isPending, error, refetch } = useNotes(
    status,
    PAGE_SIZE,
    page * PAGE_SIZE,
    refreshInterval,
  );

  return (
    <Stack gap="md">
      <SegmentedControl
        size="sm"
        fullWidth
        aria-label="Статус заметок"
        value={status}
        onChange={(value) => {
          setStatus(value as NoteStatusFilter);
          setPage(0);
        }}
        data={[
          { value: 'open', label: 'Открытые' },
          { value: 'closed', label: 'Закрытые' },
          { value: 'any', label: 'Все' },
        ]}
      />
      {error ? (
        <Alert color="red" variant="light" title="Не удалось загрузить заметки">
          <Stack gap="xs">
            <Text size="sm">{error.message}</Text>
            <Button size="xs" variant="default" onClick={() => void refetch()}>
              Повторить
            </Button>
          </Stack>
        </Alert>
      ) : isPending ? (
        <Stack gap="sm">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} height={64} radius="sm" />
          ))}
        </Stack>
      ) : data?.notes.length === 0 ? (
        <Center py={28}>
          <Stack gap="xs" align="center">
            <IconNotes size={26} stroke={1.5} opacity={0.5} />
            <Text c="dimmed" size="sm" ta="center">
          {page > 0 ? 'На этой странице заметок нет' : status === 'open'
            ? 'Открытых заметок нет' : status === 'closed' ? 'Закрытых заметок нет' : 'Заметок пока нет'}
            </Text>
          </Stack>
        </Center>
      ) : (
        <Accordion key={`${status}-${page}`} variant="separated">
          {data?.notes.map((note) => (
            <Accordion.Item key={note.id} value={note.id}>
              <Accordion.Control>
                <Stack gap={4}>
                  <Text size="sm" fw={500} style={{ overflowWrap: 'anywhere' }}>
                    {note.title}
                  </Text>
                  <Group gap="xs">
                    <Badge size="xs" variant="light">{KIND_LABEL[note.kind]}</Badge>
                    {note.status === 'closed' && <Badge size="xs" color="gray">Закрыта</Badge>}
                    {note.remind_on && (
                      <Text size="xs" c={note.status === 'open' && note.remind_on <= today() ? 'orange' : 'dimmed'}>
                        Напомнить: {formatDate(note.remind_on)}
                      </Text>
                    )}
                  </Group>
                </Stack>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="sm">
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {note.body || 'Без дополнительного текста'}
                  </Text>
                  <Text size="xs" c="dimmed">Создана: {formatFullDateTime(note.created_at)}</Text>
                  {note.closed_at && (
                    <Text size="xs" c="dimmed">Закрыта: {formatFullDateTime(note.closed_at)}</Text>
                  )}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
      {(page > 0 || data?.hasMore) && (
        <PageControls
          page={page}
          pageSize={PAGE_SIZE}
          shown={error || isPending ? 0 : data?.notes.length ?? 0}
          hasMore={!isPending && !error && (data?.hasMore ?? false)}
          onPageChange={setPage}
        />
      )}
    </Stack>
  );
}
